import { ReactNode } from "react";

import { Action } from "types/generated";

import { AccessSection, FormValues } from "./types";
import { PERMISSIONS } from "../constants";

const RAW_CONFIG = [
  {
    id: "stacks",
    title: "Stacks",
    permissions: [
      Action.StackManage,
      Action.StackDelete,
      Action.StackLock,
      Action.StackUnlock,
      Action.StackUnlockForce,
      Action.StackAddConfig,
      Action.StackDeleteConfig,
    ],
  },
  {
    title: "Runs",
    id: "run",
    permissions: [
      Action.RunTrigger,
      Action.RunTriggerWithCustomRuntimeConfig,
      Action.RunConfirm,
      Action.RunDiscard,
      Action.RunReview,
    ],
  },
  {
    id: "space",
    title: "Space",
    permissions: [Action.SpaceRead],
  },
  {
    id: "tasks",
    title: "Tasks",
    permissions: [Action.TaskCreate],
  },
];

const RAW_CONFIG_V2 = [
  {
    id: "stacks",
    title: "Stacks",
    permissions: [
      Action.StackManage,
      Action.StackDelete,
      Action.StackLock,
      Action.StackUnlock,
      Action.StackUnlockForce,
      Action.StackAddConfig,
      Action.StackDeleteConfig,
    ],
  },
  {
    title: "Runs",
    id: "run",
    permissions: [
      Action.RunTrigger,
      Action.RunTriggerWithCustomRuntimeConfig,
      Action.RunConfirm,
      Action.RunDiscard,
      Action.RunReview,
    ],
  },
  {
    id: "space",
    title: "Space",
    permissions: [Action.SpaceRead],
  },
  {
    id: "tasks",
    title: "Tasks",
    permissions: [Action.TaskCreate],
  },
  {
    id: "context",
    title: "Context",
    permissions: [Action.ContextCreate, Action.ContextUpdate, Action.ContextDelete],
  },
];

export const getConfig = (isAacV2Enabled: boolean): AccessSection[] => {
  if (isAacV2Enabled) {
    return CONFIG_V2;
  }

  return CONFIG;
};

const CONFIG: AccessSection[] = RAW_CONFIG.map((section) => ({
  ...section,
  permissions: section.permissions.map((permission) => ({
    action: permission,
    id: PERMISSIONS[permission].name,
    description: PERMISSIONS[permission].description || "",
    tooltipInfo: PERMISSIONS[permission].tooltipInfo,
    tooltipInfoVariant: PERMISSIONS[permission].tooltipInfoVariant,
  })),
}));

const CONFIG_V2: AccessSection[] = RAW_CONFIG_V2.map((section) => ({
  ...section,
  permissions: section.permissions.map((permission) => ({
    action: permission,
    id: PERMISSIONS[permission].name,
    description: PERMISSIONS[permission].description || "",
    tooltipInfo: PERMISSIONS[permission].tooltipInfo,
    tooltipInfoVariant: PERMISSIONS[permission].tooltipInfoVariant,
  })),
}));

// Define which permissions are recommended (and pre-selected by default for new roles)
const RECOMMENDED_PERMISSIONS = new Set([Action.SpaceRead]);

export const getDefaultValues = (isAacV2Enabled: boolean): FormValues => {
  const config = getConfig(isAacV2Enabled);
  const defaultValues: FormValues = {
    name: "",
    description: "",
    permissions: config.reduce((acc, section) => {
      return {
        ...acc,
        [section.id]: section.permissions.reduce(
          (acc, permission) => ({
            ...acc,
            [permission.action]: RECOMMENDED_PERMISSIONS.has(permission.action),
          }),
          {}
        ),
      };
    }, {}),
  };

  return defaultValues;
};

// Helper functions to check if permission/section is recommended
export const isRecommendedPermission = (action: Action): boolean =>
  RECOMMENDED_PERMISSIONS.has(action);

export const isRecommendedSection = (sectionId: string, isAacV2Enabled: boolean): boolean =>
  getConfig(isAacV2Enabled)
    .find((section) => section.id === sectionId)
    ?.permissions.some((permission) => RECOMMENDED_PERMISSIONS.has(permission.action)) || false;

// Helper function to get warning message for a specific permission when unchecked
export const getPermissionWarningMessage = (
  action: Action,
  isChecked: boolean
): ReactNode | undefined => {
  if (isChecked) return undefined;

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (action) {
    case Action.SpaceRead:
      return (
        <>
          Needed to access space-scoped resources unless custom role is combined with the system{" "}
          <strong>"Space Read"</strong> role.
        </>
      );
    default:
      return undefined;
  }
};
